import { awaitNewAnimationFrame, sleep } from '../artwork/lib/anim'
import { useEffect, useRef, useState } from 'react'
import Stats from 'stats.js.fps'
import {
  backgroundGradient,
  getCurrentPalette,
  resetPalette
} from '../artwork/palette'
import {
  Cell,
  CellKind,
  CellState,
  Neighbours,
  RGB,
  Rule,
  StepChances
} from '../artwork/types'
import { random } from '../artwork/lib/random'
import { viewport } from '../artwork/lib/viewport'
import { mul2 } from '../artwork/lib/math'
import {
  BACKGROUND_COLOUR_EASING,
  COLOUR_EASING,
  FADE_SPEED,
  HORIZONTAL_MARGIN_PERCENTAGE
} from '../artwork/const'
import { getMatchingRule } from '../artwork/rules'
import { paused, settings } from '../artwork/settings'

export const stats = new Stats()

if (settings.showStatistics) {
  stats.showPanel(0)
  document.body.appendChild(stats.dom)
}

interface Props {
  rules: Rule[]
  seed: number
  resolution: number
}

let grid: Cell[] = []
let cellsState: CellState[] = []
let maxUpdateIndex = 0
let canvas: HTMLCanvasElement
let gl: WebGLRenderingContext
let shaderProgram: WebGLProgram
let positionBuffer: WebGLBuffer
let positionAttributeLocation: number
let colorUniformLocation: WebGLUniformLocation
let alphaUniformLocation: WebGLUniformLocation
let transformUniformLocation: WebGLUniformLocation
let resolutionUniformLocation: WebGLUniformLocation

window.addEventListener('resize', () => {
  window.location.reload()
})

// Vertex shader - transforms vertices and passes data to fragment shader
const vertexShaderSource = `
  attribute vec2 a_position;
  uniform vec2 u_resolution;
  uniform mat3 u_transform;
  
  void main() {
    // Apply transformation matrix
    vec2 transformed = (u_transform * vec3(a_position, 1.0)).xy;
    
    // Convert from pixels to clip space (-1 to 1)
    vec2 clipSpace = (transformed / u_resolution) * 2.0 - 1.0;
    
    // Flip y axis (WebGL has origin at bottom-left)
    gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);
  }
`

// Fragment shader - determines pixel color
const fragmentShaderSource = `
  precision mediump float;
  uniform vec3 u_color;
  uniform float u_alpha;
  
  void main() {
    gl_FragColor = vec4(u_color, u_alpha);
  }
`

function createShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string
): WebGLShader {
  const shader = gl.createShader(type)!
  gl.shaderSource(shader, source)
  gl.compileShader(shader)

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    throw new Error('Failed to compile shader')
  }

  return shader
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
): WebGLProgram {
  const program = gl.createProgram()!
  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    throw new Error('Failed to link program')
  }

  return program
}

function initWebGL(canvas: HTMLCanvasElement): WebGLRenderingContext {
  const glContext = canvas.getContext('webgl', {
    alpha: false,
    antialias: true,
    premultipliedAlpha: false
  })

  if (!glContext) {
    throw new Error('WebGL not supported')
  }

  return glContext
}

function setupShaders(gl: WebGLRenderingContext): void {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
  const fragmentShader = createShader(
    gl,
    gl.FRAGMENT_SHADER,
    fragmentShaderSource
  )

  shaderProgram = createProgram(gl, vertexShader, fragmentShader)

  // Get attribute and uniform locations
  positionAttributeLocation = gl.getAttribLocation(shaderProgram, 'a_position')
  colorUniformLocation = gl.getUniformLocation(shaderProgram, 'u_color')!
  alphaUniformLocation = gl.getUniformLocation(shaderProgram, 'u_alpha')!
  transformUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_transform'
  )!
  resolutionUniformLocation = gl.getUniformLocation(
    shaderProgram,
    'u_resolution'
  )!

  // Create position buffer for triangle vertices
  positionBuffer = gl.createBuffer()!
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)

  // Triangle vertices (centered at origin)
  // This matches the original: moveTo(+r, -r), lineTo(+r, +r), lineTo(-r, +r)
  const triangleVertices = new Float32Array([
    1,
    -1, // top-right
    1,
    1, // bottom-right
    -1,
    1 // bottom-left
  ])
  gl.bufferData(gl.ARRAY_BUFFER, triangleVertices, gl.STATIC_DRAW)
}

// Create a 3x3 transformation matrix for translation, rotation, and scale
function createTransformMatrix(
  translateX: number,
  translateY: number,
  rotation: number,
  scale: number
): Float32Array {
  const cos = Math.cos(rotation)
  const sin = Math.sin(rotation)

  // Combined transformation matrix: translate * rotate * scale
  // Column-major order for WebGL
  return new Float32Array([
    cos * scale,
    sin * scale,
    0,
    -sin * scale,
    cos * scale,
    0,
    translateX,
    translateY,
    1
  ])
}

export default function Artwork({ rules, seed, resolution }: Props) {
  const container = useRef<HTMLDivElement>(null)
  const [initialized, setInitialized] = useState(false)
  const rulesRef = useRef(rules)
  const seedRef = useRef(seed)
  useEffect(() => { rulesRef.current = rules; seedRef.current = seed })

  useEffect(() => {
    grid = createGrid(rules, (seed * 0x13) << 0)
    grid = addGroupNumbers(grid)
    maxUpdateIndex = 0
  }, [rules, seed])

  useEffect(() => {
    grid = createGrid(rulesRef.current, (seedRef.current * 0x13) << 0)
    grid = addGroupNumbers(grid)
    maxUpdateIndex = 0
    cellsState.length = 0
    for (let i = 0; i < grid.length; i++) {
      cellsState[i] = { alpha: 0, angle: 0, color: [0, 0, 0], completed: false }
    }
  }, [resolution])

  useEffect(() => {
    if (random(++seed, 99) > 25) {
      resetPalette(seed)
    }
  }, [seed])

  function setCanvasDimensions() {
    const vp = viewport()
    canvas.width = vp[0] * window.devicePixelRatio
    canvas.height = vp[1] * window.devicePixelRatio
    canvas.style.width = `${vp[0]}px`
    canvas.style.height = `${vp[1]}px`
    canvas.style.position = 'fixed'
    canvas.style.left = '0px'
    canvas.style.top = '0px'

    if (gl) {
      gl.viewport(0, 0, canvas.width, canvas.height)
    }
  }

  useEffect(() => {
    if (!container.current || initialized || !grid) return
    setInitialized(true)

    canvas = document.createElement('canvas')
    setCanvasDimensions()

    // Initialize WebGL
    gl = initWebGL(canvas)
    setupShaders(gl)

    // Enable blending for alpha support
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    const gradientProgram = gl.createProgram()!

    const gradientVertShader = gl.createShader(gl.VERTEX_SHADER)!
    gl.shaderSource(
      gradientVertShader,
      `
    attribute vec2 position;
    varying vec2 vUv;
    void main() {
      vUv = position * 0.5 + 0.5;
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `
    )
    gl.compileShader(gradientVertShader)

    const gradientFragShader = gl.createShader(gl.FRAGMENT_SHADER)!
    gl.shaderSource(
      gradientFragShader,
      `
    precision mediump float;
    varying vec2 vUv;
    uniform vec3 colorBottom;
    uniform vec3 colorTop;
    void main() {
      vec3 color = mix(colorBottom, colorTop, vUv.y);
      gl_FragColor = vec4(color, 1.0);
    }
  `
    )
    gl.compileShader(gradientFragShader)
    gl.attachShader(gradientProgram, gradientVertShader)
    gl.attachShader(gradientProgram, gradientFragShader)
    gl.linkProgram(gradientProgram)
    const colorBottomLoc = gl.getUniformLocation(gradientProgram, 'colorBottom')
    const colorTopLoc = gl.getUniformLocation(gradientProgram, 'colorTop')
    const quadBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    )
    const positionLoc = gl.getAttribLocation(gradientProgram, 'position')

    function drawGradient(
      colorBottom: [number, number, number],
      colorTop: [number, number, number]
    ) {
      gl.useProgram(gradientProgram)
      // Convert 0-255 values to 0-1 range
      gl.uniform3f(
        colorBottomLoc,
        colorBottom[0] / 255,
        colorBottom[1] / 255,
        colorBottom[2] / 255
      )
      gl.uniform3f(
        colorTopLoc,
        colorTop[0] / 255,
        colorTop[1] / 255,
        colorTop[2] / 255
      )
      gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer)
      gl.enableVertexAttribArray(positionLoc)
      gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }

    container.current.appendChild(canvas)
    setDimensions()

    function draw() {
      updateAllCellsState(grid!, cellsState, maxUpdateIndex)
      if (maxUpdateIndex < cellsState.length) {
        maxUpdateIndex += ((5 / speedRatio()) * cellsState.length) / 800
      }
      drawCellsWebGL(gl, grid!, cellsState)
    }

    async function theLoop(): Promise<void> {
      let introCompleted = false
      setTimeout(() => {
        introCompleted = true
      }, 2000)

      let currentBackground: [RGB, RGB] = structuredClone(backgroundGradient)

      function transitionBackgroundGradient() {
        for (let colour = 0; colour <= 1; colour++) {
          for (let channel = 0; channel < 3; channel++) {
            currentBackground[colour][channel] +=
              (backgroundGradient[colour][channel] -
                currentBackground[colour][channel]) /
              BACKGROUND_COLOUR_EASING /
              speedRatio()
          }
        }
      }

      while (true) {
        stats.begin()

        // Clear with gray background (127/255 ≈ 0.498)
        // gl.clearColor(0.498, 0.498, 0.498, 1.0)
        transitionBackgroundGradient()
        drawGradient(...currentBackground)

        if (introCompleted) draw()

        await awaitNewAnimationFrame()
        stats.end()
        while (paused) {
          await sleep(10)
        }
      }
    }

    console.log('the loop')
    theLoop()
  }, [container, initialized])

  return (
    <div
      ref={container}
      style={{
        boxSizing: 'border-box',
        background: 'white',
        padding: '10px'
      }}
    />
  )
}

const DEFAULT_FPS = 30
export function fps(): number {
  return Math.round(stats.fps) || DEFAULT_FPS
}

export function speedRatio(): number {
  return fps() / DEFAULT_FPS
}

// 0 = _
// 1 = ◢
// 2 = ◣
// 3 = ◥
// 4 = ◤
// 5 = []

interface State {
  cols: number
  rows: number
  verticalMargin: number
  horizontalMargin: number
  cellSize: number
}

export let animationState: State | undefined

export function setDimensions(): void {
  const vp = mul2(viewport(), window.devicePixelRatio)
  const horizontalMargin = (vp[0] * HORIZONTAL_MARGIN_PERCENTAGE) / 100

  const horizontalSpaceWithinMargins = vp[0] - horizontalMargin * 2
  const verticalSpaceWithinMargins = vp[1] - horizontalMargin * 2
  const withMarginsRatio =
    horizontalSpaceWithinMargins / verticalSpaceWithinMargins
  const cols =
    (withMarginsRatio > 1.2 ? 32 : withMarginsRatio < 0.7 ? 24 : 24) *
    settings.resolution
  const rows = Math.round(cols / withMarginsRatio)
  const cellSize = (vp[0] - horizontalMargin * 2) / cols

  const heightTakenUpByCells = rows * cellSize
  const verticalMargin = (vp[1] - heightTakenUpByCells) / 2
  animationState = {
    verticalMargin,
    rows,
    horizontalMargin,
    cellSize,
    cols
  }
}

const TRANSITION_SPEED_DIV = 50

function cellAt(grid: Cell[], x: number, y: number): Cell {
  const cell = grid[x + y * animationState!.cols]
  if (
    cell === undefined ||
    y < 0 ||
    x < 0 ||
    x >= animationState!.cols ||
    y >= animationState!.rows
  )
    return { kind: 0, group: null }
  return cell
}

function sampleNextStep(array: StepChances, seed: number): CellKind {
  const options: number[] = []
  array.forEach((element, index) => {
    for (let i = 0; i < element * 10; i++) {
      options.push(index)
    }
  })
  const randomIndex = Math.round(random(seed, options.length - 1))
  return options[randomIndex] as CellKind
}

export function getNextCellKind(
  neighbours: Neighbours,
  seed: number,
  rules: Rule[]
): CellKind {
  const matchingRule = getMatchingRule(neighbours, rules)
  return sampleNextStep(matchingRule.chances, seed)
}

function getNextCell(
  grid: Cell[],
  rules: Rule[],
  x: number,
  y: number,
  seed: number
): Cell {
  const stepLeft = cellAt(grid, x - 1, y)
  const step2Left = cellAt(grid, x - 2, y)
  const stepAbove = cellAt(grid, x, y - 1)
  const stepAboveRight = cellAt(grid, x + 1, y - 1)
  const neighbours: Neighbours = [
    stepLeft.kind,
    stepAbove.kind,
    stepAboveRight.kind,
    step2Left.kind
  ]
  const selectFrom = getNextCellKind(neighbours, seed, rules)
  if (selectFrom === undefined) {
    throw new Error('cannot find next possible cell step')
  }

  return {
    kind: selectFrom,
    group: null
  }
}

export function createGrid(rules: Rule[], seed: number): Cell[] {
  setDimensions()
  const result: Cell[] = []
  let newSeed = seed
  for (let y = 0; y < animationState!.rows; y++) {
    for (let x = 0; x < animationState!.cols; x++) {
      newSeed++
      let nextCell = getNextCell(result, rules, x, y, newSeed)
      result[x + y * animationState!.cols] = nextCell
    }
  }
  return result
}

function getTargetAngle(kind: CellKind): number {
  switch (kind) {
    case 0:
      return 0
    case 1:
      return 0
    case 2:
      return (90 / 360) * Math.PI * 2
    case 3:
      return (-90 / 360) * Math.PI * 2
    case 4:
      return (180 / 360) * Math.PI * 2
    default:
      throw new Error('unknown cellkind, cannot determine getTargetAngle')
  }
}

export function updateAllCellsState(
  grid: Cell[],
  cellState: CellState[],
  maxIndex: number
): void {
  for (let i = 0; i < Math.min(maxIndex, cellState.length); i++) {
    const cell = grid[i]
    updateCellState(cell, cellState[i])
  }
}

function updateCellStateColour(cell: Cell, cellState: CellState) {
  const targetColour = getCellColour(cell.group)
  const currentColour: RGB = cellState.color
  currentColour[0] +=
    (targetColour[0] - currentColour[0]) / COLOUR_EASING / speedRatio()
  currentColour[1] +=
    (targetColour[1] - currentColour[1]) / COLOUR_EASING / speedRatio()
  currentColour[2] +=
    (targetColour[2] - currentColour[2]) / COLOUR_EASING / speedRatio()
}

function updateCellState(cell: Cell, cellState: CellState): void {
  const directionAngle = getTargetAngle(cell.kind) > cellState.angle ? 1 : -1

  if (Math.abs(getTargetAngle(cell.kind) - cellState.angle) > 0.01) {
    cellState.angle += directionAngle / TRANSITION_SPEED_DIV / speedRatio()
  } else {
    cellState.angle = getTargetAngle(cell.kind)
    updateCellStateColour(cell, cellState)
  }
  if (cell.kind === 0) {
    if (cellState.alpha > 0) {
      cellState.alpha -= FADE_SPEED / speedRatio()
    } else {
      cellState.alpha = 0
    }
  } else {
    if (cellState.alpha < 1) {
      cellState.alpha += FADE_SPEED / speedRatio()
    } else {
      cellState.alpha = 1
    }
  }
}

export function drawCellsWebGL(
  gl: WebGLRenderingContext,
  grid: Cell[],
  cellState: CellState[]
): void {
  gl.useProgram(shaderProgram)

  // Set resolution uniform
  gl.uniform2f(resolutionUniformLocation, canvas.width, canvas.height)

  // Bind position buffer and set up attribute
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  gl.enableVertexAttribArray(positionAttributeLocation)
  gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0)

  const { cellSize, horizontalMargin, verticalMargin } = animationState!
  const r = Math.ceil(cellSize / 2)

  for (let i = 0; i < cellState.length; i++) {
    const state = cellState[i]
    const alpha = Math.max(0, Math.min(state.alpha, 1))

    // Skip fully transparent cells for performance
    if (alpha <= 0) continue

    const x = i % animationState!.cols
    const y = (i / animationState!.cols) << 0

    const centerX = Math.floor(x * cellSize + cellSize / 2 + horizontalMargin)
    const centerY = Math.floor(y * cellSize + cellSize / 2 + verticalMargin)

    // Create transformation matrix
    const transform = createTransformMatrix(centerX, centerY, state.angle, r)
    gl.uniformMatrix3fv(transformUniformLocation, false, transform)

    // Set color (convert from 0-255 to 0-1)
    gl.uniform3f(
      colorUniformLocation,
      state.color[0] / 255,
      state.color[1] / 255,
      state.color[2] / 255
    )

    // Set alpha
    gl.uniform1f(alphaUniformLocation, alpha)

    // Draw the triangle
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }
}

function getCellColour(cellGroup: number | null): RGB {
  if (cellGroup === null) return [200, 200, 200]
  const currentPalette = getCurrentPalette()
  return currentPalette[cellGroup % currentPalette.length]
}

export function addGroupNumbers(grid: Cell[]): Cell[] {
  for (let i = 0; i < grid.length; i++) {
    const x = i % animationState!.cols
    const y = (i / animationState!.cols) << 0
    traverseCellToGiveGroup(grid, x, y, i)
  }
  return grid
}

function traverseCellToGiveGroup(
  grid: Cell[],
  x: number,
  y: number,
  newGroupNumber: number
): void {
  const cell = cellAt(grid, x, y)
  if (
    x < 0 ||
    x > animationState!.cols ||
    y < 0 ||
    y > animationState!.rows ||
    cell.group !== null
  ) {
    return
  }

  cell.group = newGroupNumber

  function check(dx: number, dy: number, kind: CellKind) {
    if (cellAt(grid, x + dx, y + dy).kind === kind) {
      traverseCellToGiveGroup(grid, x + dx, y + dy, newGroupNumber)
    }
  }

  switch (cell.kind) {
    case 1:
      check(1, 0, 2)
      check(1, 0, 4)
      check(0, 1, 3)
      check(0, 1, 4)
      break
    case 2:
      check(-1, 1, 1)
      check(-1, 1, 3)
      check(0, 1, 4)
      check(0, 1, 3)
      check(-1, 0, 3)
      check(-1, 0, 1)
      break
    case 3:
      check(1, 0, 4)
      check(1, 0, 2)
      check(0, -1, 2)
      check(0, -1, 1)
      break
    case 4:
      check(0, -1, 2)
      check(0, -1, 1)
      check(-1, 0, 1)
      check(-1, 0, 3)
      break
  }
}
